import React, { useCallback, useEffect, useReducer, useRef, useState } from "react"
import {
    atualizarLayoutImpressao,
    criarLayoutImpressao,
    excluirLayoutImpressao,
    listarLayoutsImpressao,
} from "../../../services/configuracoes-impressora"
import useLoadingStore from "../../../stores/LoadingStore"
import { Loading } from "../../RegistradorDePedidos"
import { ParametrosGerais, TextoModel } from "../models/textoModel"
import { TextoImpressaoActionsTypes } from "./textoImpressaoActions"
import { TextoImpressaoContext } from "./TextoImpressaoContext"
import {
    mesclarCamposComPadrao,
    ParametrosGeraisInicial,
    TextoImpressaoInicial,
} from "./textoImpressaoInicial"
import {
    getConfiguracoesDeTextoLocal,
    getLayoutAtivoIdLocal,
    getLayoutsImpressaoLocal,
    setConfiguracoesDeTextoLocal,
    setLayoutAtivoIdLocal,
    setLayoutsImpressaoLocal,
} from "./textoImpressaoLocal"
import { textoImpressaoReducer } from "./textoImpressaoReducer"

type textoImpressaoContextProviderProps = {
    children: React.ReactNode
}

type LayoutBackend = {
    id?: number
    nome?: string
    parametrosGerais: ParametrosGerais
    campos: Omit<TextoModel, "id" | "nome" | "parametrosGerais">
}

function layoutParaModelo(layout: LayoutBackend): TextoModel {
    const pg = layout.parametrosGerais
    return {
        id: layout.id !== undefined ? String(layout.id) : undefined,
        nome: layout.nome ?? TextoImpressaoInicial.nome,
        parametrosGerais: {
            ...ParametrosGeraisInicial,
            ...pg,
            exibirLogo: Boolean(pg?.exibirLogo),
            linhaAposProduto: Boolean(pg?.linhaAposProduto),
            detalharDescontoEntrega: Boolean(pg?.detalharDescontoEntrega),
            imprimirAcentuacao: pg?.imprimirAcentuacao !== false,
        },
        ...mesclarCamposComPadrao(layout.campos),
    }
}

function modeloParaPayload(modelo: TextoModel): LayoutBackend {
    const { id, nome, parametrosGerais, ...campos } = modelo
    const pg: ParametrosGerais = {
        ...parametrosGerais,
        exibirLogo: Boolean(parametrosGerais.exibirLogo),
        linhaAposProduto: Boolean(parametrosGerais.linhaAposProduto),
        detalharDescontoEntrega: Boolean(parametrosGerais.detalharDescontoEntrega),
        imprimirAcentuacao: parametrosGerais.imprimirAcentuacao !== false,
    }
    return id !== undefined
        ? { id: Number(id), nome, parametrosGerais: pg, campos }
        : { nome, parametrosGerais: pg, campos }
}

export function TextoImpressaoContextProvider({ children }: textoImpressaoContextProviderProps) {
    const [textoImpressao, dispatchTexoImpressao] = useReducer(
        textoImpressaoReducer,
        TextoImpressaoInicial
    )
    const [layouts, setLayouts] = useState<TextoModel[]>([])
    const [layoutAtivoId, setLayoutAtivoId] = useState<string | undefined>(undefined)
    const [carregando, setCarregando] = useState(false)
    const localSnapshot = useRef(getConfiguracoesDeTextoLocal())
    const setLoading = useLoadingStore(s => s.setLoading)

    useEffect(() => {
        setConfiguracoesDeTextoLocal(textoImpressao)
    }, [textoImpressao])

    const ativarLayout = useCallback((layout: TextoModel) => {
        setLayoutAtivoId(layout.id)
        setLayoutAtivoIdLocal(layout.id)
        dispatchTexoImpressao({ type: TextoImpressaoActionsTypes.INICIAL, payload: layout })
    }, [])

    const carregarDoServidor = useCallback(async () => {
        const autorizacao = localStorage.getItem("chaveSistema")
        if (!autorizacao) return

        try {
            setLoading(true)
            let response: any = await listarLayoutsImpressao(autorizacao)

            if (!response?.retornoErro && Array.isArray(response) && response.length === 0) {
                const criado = await criarLayoutImpressao(
                    { nome: TextoImpressaoInicial.nome },
                    autorizacao
                )
                if (!criado?.retornoErro) response = [criado]
            }

            if (!response?.retornoErro && Array.isArray(response) && response.length) {
                const layoutsCarregados = response.map(layoutParaModelo)
                setLayouts(layoutsCarregados)
                setLayoutsImpressaoLocal(layoutsCarregados)

                const idSalvo = getLayoutAtivoIdLocal()
                const layoutAtivo =
                    layoutsCarregados.find(l => l.id === idSalvo) ?? layoutsCarregados[0]

                ativarLayout(layoutAtivo)
                return
            }

            const layoutsLocais = Object.values(getLayoutsImpressaoLocal())
            if (layoutsLocais.length) {
                setLayouts(layoutsLocais)
                const idSalvo = getLayoutAtivoIdLocal()
                const layoutAtivo = layoutsLocais.find(l => l.id === idSalvo) ?? layoutsLocais[0]
                ativarLayout(layoutAtivo)
                return
            }

            dispatchTexoImpressao({
                type: TextoImpressaoActionsTypes.INICIAL,
                payload: localSnapshot.current,
            })
        } catch (erro) {
        } finally {
            setLoading(false)
        }
    }, [ativarLayout, setLoading])

    useEffect(() => {
        carregarDoServidor()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const salvarConfiguracoes = useCallback(async () => {
        const autorizacao = localStorage.getItem("chaveSistema")
        if (!autorizacao || !layoutAtivoId) return

        setCarregando(true)
        try {
            const {
                id: _idPayload,
                nome: _nomePayload,
                ...dados
            } = modeloParaPayload(textoImpressao)
            const response = await atualizarLayoutImpressao(layoutAtivoId, dados, autorizacao)

            if (response.retornoErro) {
                throw new Error(response.mensagem)
            }

            const layoutAtualizado = layoutParaModelo(response)
            dispatchTexoImpressao({
                type: TextoImpressaoActionsTypes.ATUALIZAR,
                payload: layoutAtualizado,
            })
            setLayouts(prev => {
                const atualizados = prev.map(l =>
                    l.id === layoutAtualizado.id ? layoutAtualizado : l
                )
                setLayoutsImpressaoLocal(atualizados)
                return atualizados
            })
        } finally {
            setCarregando(false)
        }
    }, [layoutAtivoId, textoImpressao])

    const selecionarLayout = useCallback(
        (id: string) => {
            const layout = layouts.find(l => l.id === id)
            if (layout) ativarLayout(layout)
        },
        [layouts, ativarLayout]
    )

    const criarLayout = useCallback(
        async (nome: string) => {
            const autorizacao = localStorage.getItem("chaveSistema")
            if (!autorizacao) return

            setCarregando(true)
            try {
                const response = await criarLayoutImpressao({ nome }, autorizacao)
                if (response.retornoErro) throw new Error(response.mensagem)

                const novoLayout = layoutParaModelo(response)
                setLayouts(prev => {
                    const atualizados = [...prev, novoLayout]
                    setLayoutsImpressaoLocal(atualizados)
                    return atualizados
                })
                ativarLayout(novoLayout)
            } finally {
                setCarregando(false)
            }
        },
        [ativarLayout]
    )

    const duplicarLayout = useCallback(async () => {
        const autorizacao = localStorage.getItem("chaveSistema")
        if (!autorizacao || !layoutAtivoId) return

        setCarregando(true)
        try {
            const payload = modeloParaPayload(textoImpressao)
            const response = await criarLayoutImpressao(
                {
                    nome: `${textoImpressao.nome} (cópia)`,
                    parametrosGerais: payload.parametrosGerais,
                    campos: payload.campos,
                },
                autorizacao
            )
            if (response.retornoErro) throw new Error(response.mensagem)

            const novoLayout = layoutParaModelo(response)
            setLayouts(prev => {
                const atualizados = [...prev, novoLayout]
                setLayoutsImpressaoLocal(atualizados)
                return atualizados
            })
            ativarLayout(novoLayout)
        } finally {
            setCarregando(false)
        }
    }, [layoutAtivoId, textoImpressao, ativarLayout])

    const renomearLayout = useCallback(
        async (novoNome: string) => {
            const autorizacao = localStorage.getItem("chaveSistema")
            if (!autorizacao || !layoutAtivoId) return

            setCarregando(true)
            try {
                const response = await atualizarLayoutImpressao(
                    layoutAtivoId,
                    { nome: novoNome },
                    autorizacao
                )
                if (response.retornoErro) throw new Error(response.mensagem)

                const layoutAtualizado = layoutParaModelo(response)
                setLayouts(prev => {
                    const atualizados = prev.map(l =>
                        l.id === layoutAtualizado.id ? layoutAtualizado : l
                    )
                    setLayoutsImpressaoLocal(atualizados)
                    return atualizados
                })
                dispatchTexoImpressao({
                    type: TextoImpressaoActionsTypes.ATUALIZAR,
                    payload: layoutAtualizado,
                })
            } finally {
                setCarregando(false)
            }
        },
        [layoutAtivoId]
    )

    const excluirLayout = useCallback(
        async (id: string) => {
            const autorizacao = localStorage.getItem("chaveSistema")
            if (!autorizacao || layouts.length <= 1) return

            setCarregando(true)
            try {
                const response: any = await excluirLayoutImpressao(id, autorizacao)
                if (response?.retornoErro) throw new Error(response.mensagem)

                const restantes = layouts.filter(l => l.id !== id)
                setLayouts(restantes)
                setLayoutsImpressaoLocal(restantes)

                if (layoutAtivoId === id) ativarLayout(restantes[0])
            } finally {
                setCarregando(false)
            }
        },
        [layouts, layoutAtivoId, ativarLayout]
    )

    return (
        <TextoImpressaoContext.Provider
            value={{
                textoImpressao,
                dispatchTexoImpressao,
                salvarConfiguracoes,
                carregando,
                layouts,
                layoutAtivoId,
                selecionarLayout,
                criarLayout,
                duplicarLayout,
                renomearLayout,
                excluirLayout,
            }}
        >
            <Loading />
            {children}
        </TextoImpressaoContext.Provider>
    )
}
